import { GameHex } from './GameHex.js';
import { edgesToArray, getAdjacentEdges, getAllEdgesBetween, getEdgeBetween, HexEdge, isSingleEdge } from "./types.js";
/**
 * GameMap wraps a MapDTO and provides convenient APIs for accessing map data.
 * Supports odd-r staggered rows (odd rows are indented).
 */
export class GameMap {
    constructor(mapData, movementCosts) {
        this.mapData = mapData;
        this.movementCosts = movementCosts;
        this.hexData = Array.from({ length: this.mapData.width }, () => new Array(this.mapData.height).fill(undefined));
        for (const hex of this.mapData.hexes) {
            this.hexData[hex.x][hex.y] = this.createGameHex(hex);
        }
    }
    createGameHex(data) {
        return new GameHex(data);
    }
    /**
     * Get the map's width
     */
    get width() {
        return this.mapData.width;
    }
    /**
     * Get the map's height
     */
    get height() {
        return this.mapData.height;
    }
    /**
     * Get the map's seed
     */
    get seed() {
        return this.mapData.seed;
    }
    /**
     * Get a hex at the specified coordinates
     */
    getHex(x, y) {
        return this.hexData[x]?.[y];
    }
    /**
     * Get all neighbors of a hex at the specified coordinates
     * Returns an array of valid neighbors (may be less than 6 at map edges)
     */
    getNeighbors(x, y) {
        const neighbors = [];
        const offsets = this.getNeighborOffsets(y);
        for (const [dx, dy] of offsets) {
            const nx = x + dx;
            const ny = y + dy;
            const neighbor = this.getHex(nx, ny);
            if (neighbor) {
                neighbors.push(neighbor);
            }
        }
        return neighbors;
    }
    /**
     * Get a specific neighbor of a hex by direction
     * Returns undefined if the neighbor doesn't exist (e.g., at map edge)
     */
    getNeighborOffset(x, y, hexEdge) {
        const offsets = this.getNeighborOffsets(y);
        let offset;
        // Map HexEdge to offset index
        switch (hexEdge) {
            case HexEdge.NW:
                offset = offsets[0]; // NW
                break;
            case HexEdge.NE:
                offset = offsets[1]; // NE
                break;
            case HexEdge.E:
                offset = offsets[2]; // E
                break;
            case HexEdge.SE:
                offset = offsets[3]; // SE
                break;
            case HexEdge.SW:
                offset = offsets[4]; // SW
                break;
            case HexEdge.W:
                offset = offsets[5]; // W
                break;
            default:
                return undefined;
        }
        return offset;
    }
    /**
     * Get a specific neighbor of a hex by direction
     * Returns undefined if the neighbor doesn't exist (e.g., at map edge)
     */
    getNeighbor(x, y, hexEdge) {
        const offset = this.getNeighborOffset(x, y, hexEdge);
        if (!offset) {
            return undefined;
        }
        const [dx, dy] = offset;
        return this.getHex(x + dx, y + dy);
    }
    /**
     * Get neighbor offsets for odd-r staggered rows
     * Odd rows (y % 2 === 1) are indented to the right
     *
     * Returns offsets in order: [NW, NE, E, SE, SW, W]
     */
    getNeighborOffsets(y) {
        const isOddRow = y % 2 === 1;
        if (isOddRow) {
            // Odd row (indented)
            return [
                [0, -1], // NW
                [1, -1], // NE
                [1, 0], // E
                [1, 1], // SE
                [0, 1], // SW
                [-1, 0], // W
            ];
        }
        else {
            // Even row
            return [
                [-1, -1], // NW
                [0, -1], // NE
                [1, 0], // E
                [0, 1], // SE
                [-1, 1], // SW
                [-1, 0], // W
            ];
        }
    }
    /**
     * Returns true if traveling across a hex from one edge to another crosses a river.
     *
     * @param {HexEdge} from - The single edge of the hexagon from which travel begins.
     * @param {HexEdge} to - The single edge of the hexagon at which travel ends.
     * @param {HexEdge} rivers - A combined bit-field of all edges that are crossed by a river.
     * @returns {number} The sum of a and b.
     */
    isRiverCrossing(from, to, rivers) {
        if (!isSingleEdge(from) || !isSingleEdge(to)) {
            throw new Error("from and to must be single non-NONE HexEdge values.");
        }
        if (rivers === HexEdge.NONE) {
            return false;
        }
        // Optional: if you want to treat from == to as "no crossing"
        if (from === to) {
            return false;
        }
        const edgesBetweenCW = getAllEdgesBetween(from, to, true);
        const edgesBetweenCCW = getAllEdgesBetween(from, to, false);
        const hasRiverCW = (edgesBetweenCW & rivers) !== 0;
        const hasRiverCCW = (edgesBetweenCCW & rivers) !== 0;
        return hasRiverCW && hasRiverCCW;
    }
    /**
     * When attempting to move along a river bank, this method returns the hex edge you should
     * shy towards in order to avoid crossing the river.
     *
     */
    getRiverAdjustedEnterEdge(start, end, prevEnterEdge) {
        const enterEdge = getEdgeBetween(end.x, end.y, start.x, start.y);
        const exitEdge = getEdgeBetween(start.x, start.y, end.x, end.y);
        if (!start.roads) {
            if (prevEnterEdge !== HexEdge.NONE && start.rivers) {
                if ((end.rivers & enterEdge) != 0) {
                    // We can't really travel along the river. Instead imagine it like a two step
                    // traversal. First find the adjacent edge of the hex that is not across the river.
                    const adjacentEdges = edgesToArray(getAdjacentEdges(exitEdge));
                    const adjacentEdge = this.isRiverCrossing(prevEnterEdge, adjacentEdges[0], start.rivers) ? adjacentEdges[1] : adjacentEdges[0];
                    // Get these by offset rather than hex because it's OK if it's actually off the map.
                    const offset = this.getNeighborOffset(start.x, start.y, adjacentEdge);
                    if (!offset) {
                        throw new Error("Internal error: invalid river crossing");
                    }
                    return getEdgeBetween(end.x, end.y, start.x + offset[0], start.y + offset[1]);
                }
            }
        }
        return enterEdge;
    }
    /**
     * Get the movement cost for traveling from one hex to an adjacent hex.
     * Takes into account roads (cost 1), rivers (blocks crossing without a bridge),
     * and terrain-based costs.
     *
     * @param fromX - Starting hex x coordinate
     * @param fromY - Starting hex y coordinate
     * @param toX - Destination hex x coordinate
     * @param toY - Destination hex y coordinate
     * @param enterEdge - The edge the player entered the starting hex from (HexEdge.NONE if first move)
     * @returns The movement point cost, or null if the move is blocked (river crossing, bridge jump)
     */
    getMovementCost(fromX, fromY, toX, toY, enterEdge) {
        const startingHex = this.getHex(fromX, fromY);
        const endingHex = this.getHex(toX, toY);
        if (!startingHex || !endingHex) {
            throw new Error("Invalid hex coordinates");
        }
        const exitEdge = getEdgeBetween(fromX, fromY, toX, toY);
        if (startingHex.roads) {
            if (startingHex.rivers & exitEdge) {
                throw new Error("No jumping off bridges!");
            }
            // There's no such thing as roads that follow adjacent to rivers, there's always a bridge.
            // Therefore, we don't care about rivers here - you can go anywhere.
            return 1;
        }
        else {
            // Ban river crossing without a bridge
            if (enterEdge !== HexEdge.NONE && this.isRiverCrossing(enterEdge, exitEdge, startingHex.rivers)) {
                throw new Error("You need a raft to cross the river!");
            }
            // Terrain-based cost
            if (!this.movementCosts)
                return 1;
            return this.movementCosts[endingHex.terrain] ?? 1;
        }
    }
    /**
     * BFS flood-fill from (startX, startY), expanding to any neighbor that satisfies the predicate.
     * The starting hex must itself satisfy the predicate to be included.
     * Returns all reachable hexes that match, in BFS order.
     */
    floodFill(startX, startY, predicate) {
        const result = [];
        const visited = new Set();
        const queue = [{ x: startX, y: startY }];
        while (queue.length > 0) {
            const { x, y } = queue.shift();
            const key = `${x},${y}`;
            if (visited.has(key))
                continue;
            visited.add(key);
            const hex = this.getHex(x, y);
            if (!hex || !predicate(hex))
                continue;
            result.push(hex);
            for (const neighbor of this.getNeighbors(x, y)) {
                if (!visited.has(`${neighbor.x},${neighbor.y}`)) {
                    queue.push({ x: neighbor.x, y: neighbor.y });
                }
            }
        }
        return result;
    }
    /**
     * Get the underlying MapDTO
     */
    toDTO() {
        return this.mapData;
    }
}
